import type Badge from '$lib/components/atoms/badge/Badge.svelte';
import type Link from '$lib/components/atoms/link/Link.svelte';
import type Carousel from '$lib/components/molecules/carousel/Carousel.svelte';
import type TheForkWidget from '$lib/components/theFork/TheForkWidget.svelte';
import type { Price } from '$lib/components/types';
import type { ComponentProps } from 'svelte';
import type PoiImage from '../image/PoiImage.svelte';
import type {
	AccommodationMichelinPoiData,
	AccommodationPoiData,
	DateRange,
	FuelPrices,
	GasStationPoiData,
	PicturePoiCardScheme,
	PoiFilterOptionId,
	PoiImages,
	RestaurantMichelinPoiData,
	RestaurantPoiData,
	RestStopPoiData,
	TourismPoiData,
} from '../types';
import type PoiCardPriceTag from './poiCardPriceTag/PoiCardPriceTag.svelte';
import type { PoiCardFactory } from './types';
import { page } from '$app/stores';
import { getMapLayoutContext } from '$lib/components/map/map-layout-context';
import { PREFETCH_IMAGES_COUNT } from '$lib/constants/poilist';
import { getSearchParamsStore } from '$lib/services/urls/searchParams';
import { getShortDateTimeFormatter, transformStringDateToCalendarDate } from '$lib/utils/date';
import { formatPrice } from '$lib/utils/formatPrice';
import * as m from '$paraglide/messages';
import { languageTag } from '$paraglide/runtime';
import { getLocalTimeZone } from '@internationalized/date';
import { get } from 'svelte/store';
import { MAX_PRICE_CATEGORY } from '../constants';
import { getRestStopHeadlineText } from '../content-utils';
import { getBookUrl } from '../utils';
import {
	badgeColorClasses,
	badgeIconName,
	badgeText,
	bookIconName,
	colorClass,
	defaultScoreBadgeProps,
	fallBackSrc,
	mapPriceCategory,
	MAX_EAGER_CAROUSEL,
	nonHRTBadgeColor,
	restStopVariantText,
} from './constant';

export type HRTPoiData = AccommodationMichelinPoiData | AccommodationPoiData | RestaurantPoiData | RestaurantMichelinPoiData | TourismPoiData;

export function buildCardTypeBadge(scheme: PicturePoiCardScheme) {
	return {
		icon: badgeIconName[scheme],
		colorClasses: badgeColorClasses[scheme],
		text: badgeText[scheme](),
		iconPosition: 'right',
		iconClasses: 'text-4.5',
		labelClasses: 'text-4 mr-0.5',
		isSquare: true,
		size: 'sm',
		class: 'gap-0.5',
	};
}

export function buildAccommodationMichelinDistinction(poi: AccommodationMichelinPoiData, scheme: PicturePoiCardScheme): PoiCardFactory['content']['distinction'] | undefined {
	if (
		poi.distinctionStars
		&& poi.distinctionDescription
		&& poi.distinctionStars >= 1
		&& poi.distinctionStars <= 3
	) {
		return {
			repeatedIcon: {
				icon: 'michelin-key',
				amount: poi.distinctionStars,
				style: 'distinction',
				class: `${colorClass[scheme]} text-4`,
				tooltipOptions: {
					text: poi.distinctionDescription,
					placement: 'top',
				},
			},
		};
	}
	return undefined;
}

export function buildAccomodationsFooterLink(poi: AccommodationPoiData | RestaurantMichelinPoiData | RestaurantPoiData | AccommodationMichelinPoiData, scheme: PicturePoiCardScheme) {
	const searchParams = getSearchParamsStore();

	return {
		href: getBookUrl(poi, get(page), get(searchParams)) ?? '',
		target: '_blank',
		rel: 'external',
		size: 'sm',
		button: 'outline',
		color: 'light',
		icon: bookIconName[scheme],
		label: m.poi_card__actions_book(),
		ariaLabel: `${m.poi_card__actions_book()} ${poi.name}`,
	} satisfies ComponentProps<typeof Link>;
}

export function getPoiImageSrcs(images: PoiImages[]): PoiImages | undefined {
	return images.length > 0 ? images[0] : undefined;
}

export function buildRestaurantMichelinDistinctions(poi: RestaurantMichelinPoiData) {
	const distinctions: PoiCardFactory['content']['distinction'] = {};

	if (
		poi.distinctionStars
			&& poi.distinctionDescription
			&& poi.distinctionStars >= 1
			&& poi.distinctionStars <= 3
	) {
		distinctions.repeatedIcon = {
			icon: 'michelin-star',
			amount: poi.distinctionStars,
			style: 'distinction',
			class: `${colorClass.restaurantMichelin} text-4`,
			tooltipOptions: {
				text: poi.distinctionDescription,
				placement: 'top',
			},
		};
	}

	if (poi.bibGourmand && poi.distinctionDescription) {
		distinctions.repeatedIcon = {
			icon: 'bib-gourmand',
			amount: 1,
			class: `${colorClass.restaurantMichelin} text-4`,
			tooltipOptions: {
				text: poi.distinctionDescription,
				placement: 'top',
			},
		};
	}

	if (poi.greenStar) {
		distinctions.icon = {
			icon: 'green-star',
			class: 'text-green-star mr-1 text-4',
			tooltipOptions: {
				text: m.restaurantMichelin_greenStarLabel(),
				placement: 'top',
			},
		};
	}

	return Object.keys(distinctions).length > 0 ? distinctions : undefined;
}

export function buildPoiPicture(images: PoiImages[], index: number, scheme: PicturePoiCardScheme) {
	return {
		srcset: getPoiImageSrcs(images) ?? { w320: fallBackSrc[scheme] },
		fallbackSrc: fallBackSrc[scheme],
		eager: index < PREFETCH_IMAGES_COUNT,
		sizes: '320px',
		alt: '',
	} satisfies ComponentProps<typeof PoiImage>;
}

export function buildPicturePoiCarousel(images: PoiImages[], name: string, scheme: PicturePoiCardScheme, index: number = 0) {
	if (images.length === 0) {
		return undefined;
	}

	return {
		images,
		prefixAlt: name,
		ariaLabel: `${m.poi_card__carousel__aria_label()} ${name}`,
		withEagerImages: index < MAX_EAGER_CAROUSEL,
		fallbackSrc: fallBackSrc[scheme],
	} satisfies ComponentProps<typeof Carousel>;
}

export function buildHeroBadge(scheme: PicturePoiCardScheme) {
	return {
		icon: badgeIconName[scheme],
		colorClasses: badgeColorClasses[scheme],
		text: badgeText[scheme](),
		iconPosition: 'right',
		iconClasses: 'text-4.5',
		labelClasses: 'text-sm mr-0.5',
		isSquare: true,
		size: 'sm',
		class: 'gap-0.5 max-h-5',

	} satisfies ComponentProps<typeof Badge>;
}

function formatPriceTagDatesSubContent() {
	const { startDate, endDate } = get(getSearchParamsStore())?.filters?.ACCOMMODATION_AVAILABILITY_DATE_RANGE as DateRange || {};

	if (!startDate || !endDate) {
		return undefined;
	}

	const checkin = transformStringDateToCalendarDate(startDate);
	const checkout = transformStringDateToCalendarDate(endDate);

	if (!checkin || !checkout) {
		return undefined;
	}

	const formattedDateRange = getShortDateTimeFormatter(languageTag()).formatRange(checkin.toDate(getLocalTimeZone()), checkout.toDate(getLocalTimeZone()));
	const [formattedCheckin, formattedCheckout] = formattedDateRange.split('–');

	return {
		startDate: formattedCheckin,
		endDate: formattedCheckout,
		startDatetime: startDate,
		endDatetime: endDate,
	} satisfies ComponentProps<typeof PoiCardPriceTag>['dates'];
}

export function buildPriceTagPriceRange(poi: AccommodationMichelinPoiData | AccommodationPoiData | RestaurantMichelinPoiData) {
	if (!poi.priceCategory) {
		return undefined;
	}

	return {
		filledLength: poi.priceCategory.count,
		icon: `currency-${poi.priceCategory.currency}`,
		length: MAX_PRICE_CATEGORY,
		colorForFilled: 'text-coloredneutral',
		colorForUnFilled: 'text-coloredneutral-weak',
	};
}

export function buildScoreBadge(poi: AccommodationMichelinPoiData | AccommodationPoiData | RestaurantPoiData) {
	if (!poi?.review?.score) {
		return undefined;
	}

	return {
		...defaultScoreBadgeProps,
		text: String(poi.review?.score.toFixed(1)),
	};
}

export function buildAccommodationPriceTag(
	poi: AccommodationPoiData | AccommodationMichelinPoiData,
) {
	const priceTag: ComponentProps<typeof PoiCardPriceTag> = {};

	if (poi.isAvailable === null && poi.startingPrice === null && poi.priceCategory) {
		priceTag.priceRange = buildPriceTagPriceRange(poi);
	} else if (poi.isAvailable === false) {
		priceTag.mainContent = m.poi_card__non_availability();
		priceTag.dates = formatPriceTagDatesSubContent();
	} else if (poi.isAvailable && poi.startingPrice) {
		priceTag.mainContent = formatPrice({
			amount: poi.startingPrice.amount,
			currency: poi.startingPrice.currency,
			language: languageTag(),
		});
		priceTag.dates = formatPriceTagDatesSubContent();
	}

	return priceTag;
}

export function buildAccommodationDistinctions(poi: AccommodationPoiData, scheme: PicturePoiCardScheme) {
	if (!poi.stars) {
		return undefined;
	}

	return {
		repeatedIcon: {
			icon: 'grade',
			amount: poi.stars,
			style: 'grade',
			class: colorClass[scheme],
		},
	} satisfies PoiCardFactory['content']['distinction'];
}

export function buildTheForkFooter(poi: RestaurantMichelinPoiData | RestaurantPoiData) {
	if (!poi.theForkUrl) {
		return undefined;
	}

	return {
		url: poi.theForkUrl,
		color: 'light',
		variant: 'outline',
	} satisfies ComponentProps<typeof TheForkWidget>;
}

export function buildTourismDistinctions(poi: TourismPoiData, scheme: PicturePoiCardScheme) {
	if (!poi?.distinctionStars) {
		return undefined;
	}

	return {
		repeatedIcon: {
			icon: 'tourism-star',
			amount: poi.distinctionStars,
			style: 'distinction',
			class: `${colorClass[scheme]} text-4`,
			tooltipOptions: poi.distinctionDescription
				? {
						text: poi.distinctionDescription,
						placement: 'top',
					}
				: undefined,
		},
	} satisfies PoiCardFactory['content']['distinction'];
}

export function buildNonHRTPoisBadge(scheme: keyof typeof nonHRTBadgeColor) {
	return {
		icon: badgeIconName[scheme],
		color: nonHRTBadgeColor[scheme],
		isSquare: true,
		size: 'sm',
		class: 'h-6 w-6',
		iconPosition: 'right',
	} satisfies ComponentProps<typeof Badge>;
}

export function buildRestaurantPriceTag(averagePrice: Price | null) {
	if (!averagePrice) {
		return undefined;
	}

	return {
		mainContent: formatPrice({
			amount: averagePrice.amount,
			currency: averagePrice.currency,
			language: languageTag(),
		}),
		subContent: m.poi_card__price_tag_average(),
	} satisfies ComponentProps<typeof PoiCardPriceTag>;
}

export function buildPoiHRTImageOrCarousel(poi: AccommodationMichelinPoiData | AccommodationPoiData | RestaurantPoiData | RestaurantMichelinPoiData | TourismPoiData, index: number, scheme: PicturePoiCardScheme) {
	if (poi.images.length <= 1) {
		return {
			image: buildPoiPicture(poi.images, index, scheme),
		};
	}

	return {
		carousel: buildPicturePoiCarousel(poi.images, poi.name, scheme, index),
	};
}

export function getFuelsAndGasStationFiltersAndLastUpdated(poi: GasStationPoiData) {
	const fuelPricesMap = (Array.isArray(poi.fuels?.prices) && poi.fuels.prices.length > 0 ? poi.fuels.prices.map(fuel => fuel) : []) as FuelPrices;
	const gasStationTypeFilter = get(getSearchParamsStore()).filters?.GAS_STATION_TYPE;
	const lastUpdated = poi.fuels?.lastUpdatedAt ? new Date(poi.fuels?.lastUpdatedAt) : undefined;

	return { fuelPricesMap, gasStationTypeFilter, lastUpdated };
}

export function buildGasStationContent(fuelPricesMap: FuelPrices, gasStationTypeFilter: PoiFilterOptionId[] | undefined, lastUpdated: Date | undefined) {
	const fuelPrices = gasStationTypeFilter
		? fuelPricesMap.filter(fuel => (gasStationTypeFilter).includes(fuel.filterOptionId as PoiFilterOptionId))
		: fuelPricesMap;
	const totalLength = fuelPricesMap.length;

	return {
		fuelPrices,
		lastUpdated,
		hiddenLength: gasStationTypeFilter ? totalLength - fuelPrices.length : undefined,
	} satisfies PoiCardFactory['content']['gasStation'];
}

export function buildGasStationBadgeDot(fuelPricesMap: FuelPrices, gasStationTypeFilter: PoiFilterOptionId[] | undefined) {
	const { poiMarkerWithDetailsEnabled } = getMapLayoutContext();

	if (poiMarkerWithDetailsEnabled && gasStationTypeFilter && gasStationTypeFilter?.length === 1) {
		const { priceCategory } = fuelPricesMap.find(fuel => fuel.filterOptionId === gasStationTypeFilter[0]) || {};
		const dotColorClasse = priceCategory ? mapPriceCategory[priceCategory] : undefined;

		return dotColorClasse
			? `h-6 w-6 dot-${dotColorClasse} dot`
			: dotColorClasse;
	}

	return undefined;
}

export function buildRestStopSubtitle(poi: RestStopPoiData) {
	if (poi?.variant) {
		return {
			subtitle: restStopVariantText[poi.variant.toLowerCase() as keyof typeof restStopVariantText](),
		} satisfies PoiCardFactory['content']['restStop'];
	}

	return undefined;
}

export function buildRestStopHighlight(poi: RestStopPoiData) {
	if (poi.isFamilyFriendly) {
		return {
			highlight: {
				type: 'kleber' as const,
				text: getRestStopHeadlineText(poi) || m.kleber__restStop__description(),
			},
		};
	}
}
